// Client-side validation mirroring the PRR-48 server rules (the server remains authoritative).
// Pure functions returning field-keyed error maps so forms can show per-field messages before
// submitting. Kept framework-free for easy unit testing.

import { EMAIL_CONTACT_TYPES, GENDER_VALUES, TITLE_VALUES, type ContactType } from './workersApiTypes';

export type FieldErrors<K extends string = string> = Partial<Record<K, string>>;

// RFC 5322-ish pragmatic email check (mirrors typical backend EmailAddressAttribute behaviour).
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// E.164: optional leading +, then 8–15 digits.
const E164_RE = /^\+?[0-9]{8,15}$/;

export const isPastDate = (value: string | null | undefined): boolean => {
  if (!value) return false;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return false;
  return date.getTime() < Date.now();
};

const pad = (n: number) => String(n).padStart(2, '0');

/** Today's local calendar date as a YYYY-MM-DD string. */
const todayDateString = (): string => {
  const now = new Date();
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

/**
 * True when the effective date is strictly before today, so a retroactive reason is required.
 * Compared at day granularity on the local calendar date (the form supplies a YYYY-MM-DD value):
 * selecting *today* is not retroactive, and the result is timezone-stable (no UTC-midnight skew).
 */
export const isRetroactive = (effectiveFrom: string | null | undefined): boolean => {
  if (!effectiveFrom) return false;
  const datePart = effectiveFrom.slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(datePart)) return false;
  return datePart < todayDateString();
};

export interface PersonDetailDraft {
  legalFirstName?: string | null;
  legalLastName?: string | null;
  dateOfBirth?: string | null;
  gender?: string | null;
  title?: string | null;
  effectiveFrom?: string | null;
  retroactiveReason?: string | null;
}

export const validatePersonDetail = (draft: PersonDetailDraft): FieldErrors => {
  const errors: FieldErrors = {};
  if (!draft.legalFirstName?.trim()) errors.legalFirstName = 'Legal first name is required.';
  if (!draft.legalLastName?.trim()) errors.legalLastName = 'Legal last name is required.';
  if (!draft.dateOfBirth) errors.dateOfBirth = 'Date of birth is required.';
  else if (!isPastDate(draft.dateOfBirth)) errors.dateOfBirth = 'Date of birth must be in the past.';
  if (!draft.gender) errors.gender = 'Gender is required.';
  else if (!GENDER_VALUES.includes(draft.gender as never)) errors.gender = 'Select a valid gender.';
  if (draft.title && !TITLE_VALUES.includes(draft.title as never)) errors.title = 'Select a valid title.';
  if (isRetroactive(draft.effectiveFrom) && !draft.retroactiveReason?.trim())
    errors.retroactiveReason = 'A reason is required for a backdated (retroactive) change.';
  return errors;
};

export interface ContactDraft {
  contactType?: ContactType | string;
  contactValue?: string | null;
  effectiveFrom?: string | null;
  retroactiveReason?: string | null;
}

export const validateContact = (draft: ContactDraft): FieldErrors => {
  const errors: FieldErrors = {};
  if (!draft.contactType) errors.contactType = 'Contact type is required.';
  const value = draft.contactValue?.trim() ?? '';
  if (!value) errors.contactValue = 'A contact value is required.';
  else if ((draft.contactType as ContactType) in EMAIL_CONTACT_TYPES) {
    if (!EMAIL_RE.test(value)) errors.contactValue = 'Enter a valid email address.';
  } else if (draft.contactType === 'mobile' || draft.contactType === 'landline') {
    if (!E164_RE.test(value)) errors.contactValue = 'Enter a valid phone number (digits only, optional leading +).';
  }
  if (isRetroactive(draft.effectiveFrom) && !draft.retroactiveReason?.trim())
    errors.retroactiveReason = 'A reason is required for a backdated (retroactive) change.';
  return errors;
};

export interface AddressDraft {
  addressType?: string;
  addressLine1?: string | null;
  suburb?: string | null;
  stateOrRegion?: string | null;
  postcode?: string | null;
  countryCode?: string | null;
  effectiveFrom?: string | null;
  retroactiveReason?: string | null;
}

export const validateAddress = (draft: AddressDraft): FieldErrors => {
  const errors: FieldErrors = {};
  if (!draft.addressType) errors.addressType = 'Address type is required.';
  if (!draft.addressLine1?.trim()) errors.addressLine1 = 'Address line 1 is required.';
  if (!draft.suburb?.trim()) errors.suburb = 'Suburb is required.';
  if (!draft.stateOrRegion?.trim()) errors.stateOrRegion = 'State or region is required.';
  if (!draft.postcode?.trim()) errors.postcode = 'Postcode is required.';
  const country = draft.countryCode?.trim() ?? '';
  if (country && (country.length < 2 || country.length > 3)) errors.countryCode = 'Country code must be 2–3 characters.';
  if (isRetroactive(draft.effectiveFrom) && !draft.retroactiveReason?.trim())
    errors.retroactiveReason = 'A reason is required for a backdated (retroactive) change.';
  return errors;
};

export const hasErrors = (errors: FieldErrors): boolean => Object.keys(errors).length > 0;
